import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useAuthStore from '../store/authStore'
import Brush from '@mui/icons-material/Brush'
import Psychology from '@mui/icons-material/Psychology'
import AutoAwesome from '@mui/icons-material/AutoAwesome'
import School from '@mui/icons-material/School'
import LanguageSwitcher from '../components/common/LanguageSwitcher'
import { formatUiInteger } from '../utils/uiNumerals'

const STEP_ICONS = [Brush, Psychology, AutoAwesome, School]

const ASSETS = {
  logo: '/assets/images/ui_elements/logo.png',
  favicon: '/favicon.png',
  reecoStory: '/assets/images/reeco/reeco_pondering.png',
  reecoGame: '/assets/images/reeco/reeco_walking.png',
  reecoActivity: '/assets/images/reeco/reeco_watering.png',
  iconStory: '/assets/images/ui_elements/icon_story.png',
  iconGame: '/assets/images/ui_elements/icon_game.png',
  iconActivity: '/assets/images/ui_elements/icon_activity.png',
}

const LandingPage = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { isAuthenticated, clearAuth } = useAuthStore()
  const uiShort = (i18n.language || 'en').split('-')[0].toLowerCase()
  const navNeedsCompactTypography = ['nl', 'fa', 'ar'].includes(uiShort)
  const btnFontClass = navNeedsCompactTypography ? 'text-xs' : 'text-xs sm:text-sm'
  const btnSizeClass = 'min-w-[4.75rem] w-[4.75rem] sm:min-w-[6rem] sm:w-[6rem] md:min-w-[7.5rem] md:w-[7.5rem] max-w-[7.5rem]'

  const handleLogout = () => {
    clearAuth()
    navigate('/', { replace: true })
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/80 via-white to-green-50/60">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200/80 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center gap-3 h-14 sm:h-16 md:min-h-[4.5rem]">
            <Link to="/" className="flex items-center gap-2 sm:gap-3 shrink-0 min-w-0">
              <img
                src={ASSETS.favicon}
                alt={t('common.appName')}
                className="h-8 w-8 sm:h-9 sm:w-9 md:h-11 md:w-11 object-contain rounded-full shrink-0"
                onError={(e) => { e.target.src = ASSETS.logo; e.target.onerror = null; e.target.classList.remove('rounded-full') }}
              />
              <span className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 tracking-tight truncate">
                {t('common.appName')}
              </span>
            </Link>
            <nav className="flex items-center gap-2 sm:gap-3 md:gap-5 shrink-0">
              <LanguageSwitcher />
              <div className="flex items-center gap-1.5 sm:gap-2">
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/dashboard"
                      className={`inline-flex items-center justify-center py-2 md:py-2.5 font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-colors ${btnSizeClass} ${btnFontClass}`}
                    >
                      {t('common.dashboard')}
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className={`inline-flex items-center justify-center py-2 md:py-2.5 font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 shadow-sm hover:shadow transition-all ${btnSizeClass} ${btnFontClass}`}
                    >
                      {t('common.logout')}
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className={`inline-flex items-center justify-center py-2 md:py-2.5 font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-colors ${btnSizeClass} ${btnFontClass}`}
                    >
                      {t('common.login')}
                    </Link>
                    <Link
                      to="/register"
                      className={`inline-flex items-center justify-center py-2 md:py-2.5 font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 shadow-sm hover:shadow transition-all ${btnSizeClass} ${btnFontClass}`}
                    >
                      {t('common.register')}
                    </Link>
                  </>
                )}
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 md:pt-20 md:pb-24 overflow-hidden">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="text-center lg:text-start order-2 lg:order-1">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-4 md:mb-6">
              {t('landing.title')}
            </h1>
            <p className="text-base md:text-xl text-gray-600 mb-8 md:mb-10 max-w-xl mx-auto lg:mx-0">
              {t('landing.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-3">
              <Link
                to={isAuthenticated ? '/dashboard' : '/register'}
                className="inline-flex items-center justify-center px-6 py-3.5 md:px-8 md:py-4 text-base font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 shadow-lg hover:shadow-xl transition-all"
              >
                {isAuthenticated ? t('common.dashboard') : t('landing.getStarted')}
              </Link>
              <a
                href="#features"
                className="inline-flex items-center justify-center px-6 py-3.5 md:px-8 md:py-4 text-base font-semibold text-primary-600 bg-white border-2 border-primary-600 rounded-xl hover:bg-primary-50 transition-colors"
              >
                {t('landing.learnMore')}
              </a>
            </div>
          </div>
          <div className="flex justify-center order-1 lg:order-2">
            <img
              src={ASSETS.logo}
              alt={t('common.appName')}
              className="w-56 h-56 md:w-72 md:h-72 lg:w-80 lg:h-80 object-contain drop-shadow-lg"
              onError={(e) => { e.target.style.display = 'none' }}
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 scroll-mt-[5.5rem]">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center text-gray-900 mb-4">
          {t('landing.features.title', 'Features')}
        </h2>
        <p className="text-gray-600 text-center max-w-2xl mx-auto mb-12 md:mb-16">
          {t('landing.subtitle')}
        </p>
        <div className="grid md:grid-cols-3 gap-8 md:gap-10">
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-gray-100 hover:shadow-xl hover:border-primary-100 transition-all flex flex-col items-center text-center">
            <div className="w-24 h-24 md:w-28 md:h-28 mb-5 flex items-center justify-center">
              <img src={ASSETS.reecoStory} alt="" className="w-full h-full object-contain" onError={(e) => { e.target.src = ASSETS.iconStory; e.target.onerror = null }} />
            </div>
            <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-3">{t('landing.features.stories.title', 'AI-Powered Stories')}</h3>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed">
              {t('landing.features.stories.description', 'Transform drawings into personalized eco-stories featuring Reeco')}
            </p>
          </div>
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-gray-100 hover:shadow-xl hover:border-primary-100 transition-all flex flex-col items-center text-center">
            <div className="w-24 h-24 md:w-28 md:h-28 mb-5 flex items-center justify-center">
              <img src={ASSETS.reecoGame} alt="" className="w-full h-full object-contain" onError={(e) => { e.target.src = ASSETS.iconGame; e.target.onerror = null }} />
            </div>
            <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-3">{t('landing.features.games.title', 'Educational Games')}</h3>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed">
              {t('landing.features.games.description', "Simple 2D games based on your child's drawings")}
            </p>
          </div>
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-gray-100 hover:shadow-xl hover:border-primary-100 transition-all flex flex-col items-center text-center">
            <div className="w-24 h-24 md:w-28 md:h-28 mb-5 flex items-center justify-center">
              <img src={ASSETS.reecoActivity} alt="" className="w-full h-full object-contain" onError={(e) => { e.target.src = ASSETS.iconActivity; e.target.onerror = null }} />
            </div>
            <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-3">{t('landing.features.activities.title', 'Eco Activities')}</h3>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed">
              {t('landing.features.activities.description', 'Real-world activities to inspire environmental awareness')}
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="relative py-20 md:py-28 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-4xl font-extrabold text-center text-gray-900 mb-4 tracking-tight">
            {t('landing.howItWorks.title', 'How It Works')}
          </h2>
          <p className="text-gray-500 text-center max-w-xl mx-auto mb-14 md:mb-16 text-base md:text-lg">
            {t('landing.subtitle')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6 relative">
            {[
              { step: 1, title: t('landing.howItWorks.step1', 'Draw or Upload'), desc: t('landing.howItWorks.step1Desc', 'Create a drawing or upload an image') },
              { step: 2, title: t('landing.howItWorks.step2', 'AI Analysis'), desc: t('landing.howItWorks.step2Desc', 'AI analyzes your drawing') },
              { step: 3, title: t('landing.howItWorks.step3', 'Generate Content'), desc: t('landing.howItWorks.step3Desc', 'Get story, game, and activity') },
              { step: 4, title: t('landing.howItWorks.step4', 'Enjoy & Learn'), desc: t('landing.howItWorks.step4Desc', 'Explore and learn together') },
            ].map(({ step, title, desc }, index) => {
              const Icon = STEP_ICONS[index]
              return (
                <div
                  key={step}
                  className="relative group"
                >
                  <div className="relative bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-lg hover:shadow-primary-500/10 hover:border-primary-200/80 transition-all duration-300 p-6 md:p-8 text-center h-full flex flex-col items-center">
                    <div className="relative mb-5">
                      <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center mx-auto shadow-lg shadow-primary-500/25 group-hover:shadow-xl group-hover:shadow-primary-500/30 group-hover:scale-105 transition-all duration-300">
                        <Icon sx={{ fontSize: 40 }} className="text-white" />
                      </div>
                      <span className="absolute -top-2 end-2 flex h-7 w-7 items-center justify-center rounded-full bg-gray-900 text-xs font-bold text-white shadow ring-2 ring-white">
                        {step}
                      </span>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">{title}</h3>
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed flex-1">{desc}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-primary-600 to-primary-800" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.04\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-80" aria-hidden />
        <div className="relative max-w-3xl mx-auto text-center px-4">
          <h2 className="text-2xl md:text-4xl font-extrabold text-white mb-5 tracking-tight">
            {t('landing.cta.title', 'Ready to Start?')}
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-10 leading-relaxed">
            {t('landing.cta.description', "Join ReecoVerse and transform your child's creativity into learning adventures")}
          </p>
          <Link
            to={isAuthenticated ? '/dashboard' : '/register'}
            className="inline-flex items-center justify-center px-8 py-4 text-base font-bold bg-white text-primary-700 rounded-xl hover:bg-gray-100 transition-all shadow-xl hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98]"
          >
            {isAuthenticated ? t('common.dashboard') : t('landing.getStarted')}
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src={ASSETS.favicon} alt="" className="h-8 w-8 object-contain rounded-full" onError={(e) => { e.target.src = ASSETS.logo; e.target.onerror = null; e.target.classList.remove('rounded-full') }} />
                <h3 className="text-xl font-bold">{t('common.appName')}</h3>
              </div>
              <p className="text-gray-400 text-sm">
                {t('landing.footer.description', "AI-powered platform for children's eco-education")}
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('landing.footer.links', 'Quick Links')}</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/" className="hover:text-white">{t('common.home', 'Home')}</Link></li>
                {isAuthenticated ? (
                  <>
                    <li><Link to="/dashboard" className="hover:text-white">{t('common.dashboard')}</Link></li>
                    <li>
                      <button type="button" onClick={handleLogout} className="hover:text-white text-start">
                        {t('common.logout')}
                      </button>
                    </li>
                  </>
                ) : (
                  <>
                    <li><Link to="/register" className="hover:text-white">{t('common.register')}</Link></li>
                    <li><Link to="/login" className="hover:text-white">{t('common.login')}</Link></li>
                  </>
                )}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('landing.footer.contact', 'Contact')}</h4>
              <p className="text-gray-400 text-sm">{t('landing.footer.email', 'Email: info@reecoverse.com')}</p>
            </div>
          </div>
          <div className="mt-10 pt-8 border-t border-gray-800 text-center text-gray-400 text-sm">
            {t('landing.footer.copyright', {
              year: formatUiInteger(new Date().getFullYear(), uiShort),
            })}
          </div>
        </div>
      </footer>
    </div>
  )
}

export default LandingPage
